import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import {
  GamePriceSlice,
  GamePrices,
  GamePromotions,
  PriceTimestamps,
} from '@/features/games/shared/types/game-store.types';
import { PurchaseHistorySlice } from '@/features/games/shared/types/purchase-history.types';
import { createGamePriceSlice } from '@/features/games/shared/store/game-price-slice';
import { createPurchaseHistorySlice } from '@/features/games/shared/store/purchase-history.slice';
import { createWishlistsSlice } from '@/features/games/shared/store/wishlists.slice';
import { WishlistsSlice } from '@/features/games/shared/types/wishlist.types';
import { TIME_CONSTANTS_IN_MS } from '@/shared/constants/time';

export type GameStore = GamePriceSlice & PurchaseHistorySlice & WishlistsSlice;

export const useGameStore = create<GameStore>()(
  persist(
    (...a) => ({
      ...createGamePriceSlice(...a),
      ...createPurchaseHistorySlice(...a),
      ...createWishlistsSlice(...a),
    }),
    {
      name: 'game-store',
      onRehydrateStorage: () => (state) => {
        if (!state) return;
        purgeStaleGamePrices(state);
      },
    },
  ),
);

function purgeStaleGamePrices(state: GameStore): void {
  const now = Date.now();
  const prices: GamePrices = {};
  const promotions: GamePromotions = {};
  const timestamps: PriceTimestamps = {};
  let hasStale = false;
  for (const sku of Object.keys(state.timestamps)) {
    if (now - state.timestamps[sku] > TIME_CONSTANTS_IN_MS.TEN_MINUTES) {
      hasStale = true;
      continue;
    }
    timestamps[sku] = state.timestamps[sku];
    if (state.prices[sku] !== undefined) prices[sku] = state.prices[sku];
    if (state.promotions[sku] !== undefined) promotions[sku] = state.promotions[sku];
  }
  if (!hasStale) return;
  useGameStore.setState({ prices, promotions, timestamps });
}
